
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useEmailRecipients } from '../notifications/useEmailRecipients';
import { useNotificationDispatcher } from '@/hooks/useNotificationDispatcher';
import { Poll } from '@/types/poll';

export const usePollClosure = () => {
  const [isClosing, setIsClosing] = useState(false);
  const { fetchRecipientEmails } = useEmailRecipients();
  const { dispatch } = useNotificationDispatcher();
  
  const closePoll = useCallback(async (pollId: string, pollData?: Poll) => {
    setIsClosing(true);
    try {
      console.log(`Attempting to close poll: ${pollId}`);
      
      // Use the close_poll RPC function 
      const { error } = await supabase
        .rpc('close_poll', { poll_id: pollId });
      
      if (error) throw error;
      
      // If we have poll data, send notifications about poll closure
      if (pollData) {
        try {
          // Get recipients for poll notifications
          const recipients = await fetchRecipientEmails('polls');
          
          if (recipients.length > 0) {
            // Create notification data with the proper structure
            const notificationData = {
              pollData: {
                title: pollData.title,
                description: pollData.description,
                results: {
                  totalVotes: pollData.total_votes || 0,
                  topOption: pollData.options?.[0]?.text || 'No option'
                }
              }
            };
            
            // Send email notifications using the pollClosed notification type
            await dispatch('pollClosed', recipients, notificationData);
            console.log('Poll closed notifications sent to:', recipients);
          }
        } catch (notificationError) {
          // Don't fail the whole operation if notifications fail
          console.error('Error sending notifications:', notificationError);
        }
      }
      
      toast.success('Poll closed successfully');
      return true;
    } catch (error: any) {
      console.error('Error closing poll:', error);
      toast.error(error.message || 'Failed to close poll');
      return false;
    } finally {
      setIsClosing(false);
    }
  }, [fetchRecipientEmails, dispatch]);
  
  return { closePoll, isClosing };
};
